import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import {
  A4FormatDocIcon,
  CheckMark3Icon,
  CreditCardIcon,
  CriminalRecordIcon,
  DiplomaIcon,
  Email2Icon,
  EmergencyPhoneIcon,
  EtaDocumentIcon,
  FileContractIcon,
  IdTwoIcon,
  PassportIcon,
  PendingIcon,
  X2Icon
} from '@repo/ui/icons'
import { TFunction } from 'i18next'
import { ReactNode } from 'react'

import { LocaleKeys } from '../../../../locale/translation-types'
import { AffectedStaffProperties } from '../../models/affectedStaff/properties.affected-staff'
import { Contract } from '../../models/contract/contract'
import { CountryLight } from '../../models/country/country'
import { Staff, StaffDocument, StaffMissingDocument } from '../../models/staff/staff'
import { StatusOption } from '../../utils'
import { diplomaEnumMapping } from '../staff-application/diploma.enum'

export enum DocumentStatusEnum {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  VERIFIED = 'VERIFIED',
  REFUSED = 'REFUSED'
}

export enum StaffDocumentTypeEnum {
  DIPLOMA = 'DIPLOMA',
  PAY_SLIP = 'PAY_SLIP',
  CRIMINAL_RECORD = 'CRIMINAL_RECORD',
  ID_FILE = 'ID_FILE',
  ETA = 'ETA'
}

export type DocumentStatusOption = {
  label: LocaleKeys
  variant: StatusOption['variant']
  icon?: ReactNode
}

export type DocumentStatusOptionWithoutTranslation = Pick<
  DocumentStatusOption,
  'variant' | 'icon'
> & {
  label: string
}

export const documentStatusEnumMapping: Record<DocumentStatusEnum, DocumentStatusOption> = {
  PENDING: {
    label: 'document.status.enum.PENDING',
    variant: 'danger',
    icon: <PendingIcon color={tailwindColors.red.DEFAULT} />
  },
  RECEIVED: {
    label: 'document.status.enum.RECEIVED',
    variant: 'warning',
    icon: <Email2Icon color={tailwindColors.orange.DEFAULT} />
  },
  VERIFIED: {
    label: 'document.status.enum.VERIFIED',
    variant: 'success',
    icon: <CheckMark3Icon color={tailwindColors.green.DEFAULT} />
  },
  REFUSED: {
    label: 'document.status.enum.REFUSED',
    variant: 'danger',
    icon: <X2Icon color={tailwindColors.red.DEFAULT} />
  }
}

export const idStatusEnumMapping: Record<DocumentStatusEnum, DocumentStatusOption> = {
  PENDING: {
    label: 'document.status.enum.PENDING',
    variant: 'danger',
    icon: <PendingIcon color={tailwindColors.red.DEFAULT} />
  },
  RECEIVED: {
    label: 'document.status.enum.RECEIVED',
    variant: 'warning',
    icon: <CheckMark3Icon color={tailwindColors.orange.DEFAULT} />
  },
  VERIFIED: {
    label: 'document.status.enum.VERIFIED',
    variant: 'success',
    icon: <CheckMark3Icon color={tailwindColors.green.DEFAULT} />
  },
  REFUSED: {
    label: 'document.status.enum.REFUSED',
    variant: 'danger',
    icon: <X2Icon color={tailwindColors.red.DEFAULT} />
  }
}

export const staffDocumentTypeEnumMapping = (
  document: StaffDocument,
  t: TFunction<'en' | 'fr', undefined>
): DocumentStatusOptionWithoutTranslation => {
  switch (document.type) {
    case StaffDocumentTypeEnum.DIPLOMA:
      return {
        label: `Diplôme - ${t(diplomaEnumMapping[document.diploma.type])}`,
        variant: 'purple'
      }
    case StaffDocumentTypeEnum.PAY_SLIP:
      return {
        label: 'Bulletin de paie',
        variant: 'blue'
      }
    case StaffDocumentTypeEnum.CRIMINAL_RECORD:
      return {
        label: 'Casier judiciaire',
        variant: 'warning'
      }
    case StaffDocumentTypeEnum.ETA:
      return {
        label: 'ETA',
        variant: 'fushia'
      }
  }
}

export const staffMissingDocumentTypeEnumMapping: (
  document: StaffMissingDocument
) => DocumentStatusOption = (document) => {
  switch (document.type) {
    case StaffDocumentTypeEnum.DIPLOMA:
      return {
        label: diplomaEnumMapping[document.diploma.type],
        variant: 'purple'
      }
    case StaffDocumentTypeEnum.PAY_SLIP:
      return {
        label: 'staff.document.type.enum.paySlip',
        variant: 'blue'
      }
    case StaffDocumentTypeEnum.CRIMINAL_RECORD:
      return {
        label: 'staff.document.type.enum.criminalRecord',
        variant: 'warning'
      }

    case StaffDocumentTypeEnum.ID_FILE:
      return {
        label: 'staff.document.type.enum.idFile',
        variant: 'success'
      }
    case StaffDocumentTypeEnum.ETA:
      return {
        label: 'staff.document.type.enum.eta',
        variant: 'success'
      }
  }
}

export enum AllStaffDocumentTypeEnum {
  BANK = 'BANK',
  PHOTO = 'PHOTO',
  EMERGENCY_CONTACT = 'EMERGENCY_CONTACT',
  SIGNED_CONTRACT = 'SIGNED_CONTRACT',
  DIPLOMA = 'DIPLOMA',
  PAY_SLIP = 'PAY_SLIP',
  CRIMINAL_RECORD = 'CRIMINAL_RECORD',
  ID_FILE = 'ID_FILE',
  ETA = 'ETA'
}

export function getAllStaffDocumentMapping(
  staff: Staff,
  contract?: Contract,
  staffRequestCountry?: CountryLight
): Record<AllStaffDocumentTypeEnum, DocumentStatusOptionWithoutTranslation> {
  const documentStatuses = AffectedStaffProperties.getAllStaffDocumentStatus(
    staff,
    contract,
    staffRequestCountry
  )

  function getIconData(
    status: AllStaffDocumentTypeEnum,
    Icon: SVGFC
  ): Pick<DocumentStatusOptionWithoutTranslation, 'variant' | 'icon'> {
    const documentStatus = documentStatuses[status]

    if (documentStatus === DocumentStatusEnum.RECEIVED) {
      return {
        variant: 'warning' as const,
        icon: <Icon color={tailwindColors.orange.DEFAULT} />
      }
    }

    if (documentStatus === DocumentStatusEnum.PENDING) {
      return {
        variant: 'danger' as const,
        icon: <Icon color={tailwindColors.red.DEFAULT} />
      }
    }

    const variant = AffectedStaffProperties.getStatusVariant(documentStatus)
    const color = AffectedStaffProperties.getStatusColor(documentStatus)
    const icon = <Icon color={color} />
    return { variant, icon }
  }

  return {
    [AllStaffDocumentTypeEnum.CRIMINAL_RECORD]: {
      label: 'Casier',
      ...getIconData(AllStaffDocumentTypeEnum.CRIMINAL_RECORD, CriminalRecordIcon)
    },
    [AllStaffDocumentTypeEnum.PAY_SLIP]: {
      label: 'Fiche de paie',
      ...getIconData(AllStaffDocumentTypeEnum.PAY_SLIP, A4FormatDocIcon)
    },
    [AllStaffDocumentTypeEnum.ID_FILE]: {
      label: 'CNI/PP',
      ...getIconData(AllStaffDocumentTypeEnum.ID_FILE, PassportIcon)
    },
    [AllStaffDocumentTypeEnum.DIPLOMA]: {
      label: 'Diplôme',
      ...getIconData(AllStaffDocumentTypeEnum.DIPLOMA, DiplomaIcon)
    },
    [AllStaffDocumentTypeEnum.BANK]: {
      label: 'Banque',
      ...getIconData(AllStaffDocumentTypeEnum.BANK, CreditCardIcon)
    },
    [AllStaffDocumentTypeEnum.PHOTO]: {
      label: 'Photo',
      ...getIconData(AllStaffDocumentTypeEnum.PHOTO, IdTwoIcon)
    },
    [AllStaffDocumentTypeEnum.ETA]: {
      label: 'ETA',
      ...getIconData(AllStaffDocumentTypeEnum.ETA, EtaDocumentIcon)
    },
    [AllStaffDocumentTypeEnum.EMERGENCY_CONTACT]: {
      label: 'Contact d’urgence',
      ...getIconData(AllStaffDocumentTypeEnum.EMERGENCY_CONTACT, EmergencyPhoneIcon)
    },
    [AllStaffDocumentTypeEnum.SIGNED_CONTRACT]: {
      label: 'Contrat signé',
      ...getIconData(AllStaffDocumentTypeEnum.SIGNED_CONTRACT, FileContractIcon)
    }
  }
}
